import React, { useCallback, useContext } from 'react'
import { LinkButton } from '@/atoms/Button'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { TitleMD } from '@/atoms/Text'
import { ProjectMetaContext } from '@/contexts/ProjectMetaContext/ProjectMetaContext'
import { HorizontalItemScroller } from '@/molecules/HorizontalItemScroller'
import { LightFranchise } from '@/services/FranchiseService'
import { Project } from '@/services/ProjectsService'
import { useSafeTrack } from '@/utils/analytics'
import { isDefined } from '@/utils/primitives/generics'
import { useTranslate } from '@/utils/translate/translate-client'
import { FranchiseProjectCard } from './FranchiseProjectCard'

interface CollectionTabProps {
  franchise?: LightFranchise
  pagePlacement: number
}

export const CollectionTab: React.FC<CollectionTabProps> = ({ franchise, pagePlacement }) => {
  const track = useSafeTrack()
  const { t } = useTranslate('watch')
  const { project } = useContext(ProjectMetaContext)
  const franchisePageLink = franchise?.slug ? `/franchise/${franchise.slug}` : ''

  const handleLearnMoreClick = useCallback(() => {
    track('Watch Project Collection Learn More Clicked', { project: project?.slug, franchise: franchise?.slug })
  }, [franchise?.slug, project?.slug, track])

  const handleProjectClick = useCallback(
    (relatedProject: Project) => {
      track('Watch Project Franchise Project Clicked', { project: relatedProject?.slug })
    },
    [track],
  )

  return (
    <div className="h-full w-full overflow-x-hidden pt-[72px]">
      <PaddedContainer fluid>
        <TitleMD aria-label="community-tab-header" weight="bold" className="md:photon-title-md mb-4 mr-auto">
          {t('moreFromFranchise', 'More From {{ franchiseName }}', { franchiseName: franchise?.name })}
        </TitleMD>
      </PaddedContainer>
      <div className="w-full">
        <HorizontalItemScroller
          arrows
          slidesToScroll={5}
          slidesToShow={5}
          responsive={[
            {
              breakpoint: 1536,
              settings: {
                slidesToScroll: 4,
                slidesToShow: 4,
              },
            },
            {
              breakpoint: 1400,
              settings: {
                slidesToScroll: 3.5,
                slidesToShow: 3.5,
              },
            },
            {
              breakpoint: 1280,
              settings: {
                slidesToScroll: 3,
                slidesToShow: 3,
              },
            },
            {
              breakpoint: 768,
              settings: {
                slidesToScroll: 2.5,
                slidesToShow: 2.5,
              },
            },
            {
              breakpoint: 640,
              settings: {
                slidesToScroll: 2,
                slidesToShow: 2,
              },
            },
            {
              breakpoint: 480,
              settings: {
                slidesToScroll: 1.5,
                slidesToShow: 1.5,
              },
            },
          ]}
          wrapperClassName="w-full px-4 sm:px-8 md:px-12 xl:px-16"
          scrollerClassName="w-full"
          centerPadding="0px"
        >
          {franchise?.projects
            ?.filter(isDefined)
            .filter((p) => p?.id !== project?.id)
            .map((relatedProject, index) => (
              <FranchiseProjectCard
                key={relatedProject.id}
                relatedProject={relatedProject as Project}
                itemIndex={index}
                pagePlacement={pagePlacement}
                onProjectClick={handleProjectClick}
                className="pr-2"
              />
            ))}
        </HorizontalItemScroller>
        {franchise?.slug && (
          <PaddedContainer fluid>
            <div className="flex flex-col gap-4 md:flex-row">
              <LinkButton
                href={franchisePageLink}
                className="mt-10 w-fit"
                variant="white"
                outline
                onClick={handleLearnMoreClick}
              >
                {t('learnMore', 'Learn More')}
              </LinkButton>
            </div>
          </PaddedContainer>
        )}
      </div>
    </div>
  )
}
